import React, { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Smartphone,
  Users as UsersIcon,
  Monitor, MonitorPlay,
  KeyRound,
  ShieldCheck,
  Fingerprint,
  IdCard,
  Car,
  ScrollText,
  FileText,
  User,
  UserPlus,
  PanelLeftClose,
  PanelLeft,
  ExternalLink,
  Zap,
  CreditCard,
  Receipt,
  Gift,
  Landmark,
  Activity,
  Globe,
  MapPin,
  Rocket,
  Hammer,
  Settings,
  AudioLines,
  Megaphone,
} from 'lucide-react';
import './Layout.css';

// ── Nav configuration ──
// Grouped nav items — add new items or groups here.
const NAV_GROUPS = [
  {
    label: 'Main',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/loop-health', label: 'Loop Health', icon: Activity },
    ],
  },
  {
    label: 'Organisation',
    items: [
      { path: '/tenants', label: 'Tenants', icon: Building2 },
      { path: '/users', label: 'Users', icon: User },
      { path: '/add-user', label: 'Add User', icon: UserPlus },
      { path: '/audit-log', label: 'Login Audit', icon: ShieldCheck },
      { path: '/settings', label: 'Settings', icon: Settings },
    ],
  },
  {
    label: 'Billing & Payments',
    items: [
      { path: '/billing', label: 'Overview', icon: Activity },
      { path: '/billing/accounts', label: 'Accounts', icon: CreditCard },
      { path: '/billing/transactions', label: 'Transactions', icon: Receipt },
      { path: '/billing/invoices', label: 'Invoices & GST', icon: FileText },
      { path: '/billing/credits', label: 'Credits', icon: Gift },
      { path: '/billing/gateway', label: 'Payment Gateway', icon: Landmark },
    ],
  },
  {
    label: 'Moments API',
    items: [
      { path: '/moments', label: 'Key Management', icon: Globe },
      { path: '/moments/plans', label: 'Billing Plans', icon: CreditCard },
    ],
  },
  {
    label: 'Devices',
    items: [
      { path: '/devices', label: 'Devices', icon: Smartphone },
      { path: '/device-groups', label: 'Device Groups', icon: UsersIcon },
      { path: '/drivers', label: 'Drivers', icon: IdCard },
      { path: '/vehicles', label: 'Vehicles', icon: Car },
      { path: '/media-screens', label: 'Media Screens', icon: MonitorPlay },
      { path: '/ggos-devices', label: 'GGOS Devices', icon: Monitor },
      { path: '/identities', label: 'Identities', icon: Fingerprint },
    ],
  },
  {
    label: 'Release Management',
    items: [
      { path: '/releases/fleet', label: 'Fleet Versions', icon: Rocket },
      { path: '/releases/apks', label: 'APK Library', icon: FileText },
      { path: '/releases/builds', label: 'Builds', icon: Hammer },
    ],
  },
  {
    label: 'Brands',
    items: [
      { path: '/campaigns', label: 'Campaigns', icon: Megaphone },
    ],
  },
  {
    label: 'Perception',
    items: [
      { path: '/speech-labelling', label: 'Speech Labelling', icon: AudioLines },
    ],
  },
  {
    label: 'Security',
    items: [
      { path: '/licenses', label: 'Licenses', icon: KeyRound },
      { path: '/bootstrap-cas', label: 'Bootstrap CAs', icon: ShieldCheck },
    ],
  },
  {
    label: 'Hex Grid & Traffic',
    items: [
      { path: '/cities', label: 'City Management', icon: MapPin },
      { path: '/super-admin/monitoring', label: 'Super-Admin Monitoring', icon: Activity },
    ],
  },
  {
    label: 'Logs',
    items: [
      { path: '/bootstrap-auth-logs', label: 'Auth Logs', icon: ScrollText },
      { path: '/device-event-logs', label: 'Event Logs', icon: FileText },
    ],
  },
];

// Routes whose pages manage their own full-bleed layout (no padding wrapper).
const FULL_BLEED_PREFIXES = ['/ggos-devices/'];

const Layout = ({ children }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = useCallback(
    (path) => {
      if (path === '/dashboard' && location.pathname === '/') return true;
      // Exact match for /billing overview to avoid matching /billing/transactions etc.
      if (path === '/billing') return location.pathname === '/billing';
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  // Determine if the current page wants full-bleed (no content-area padding).
  const isFullBleed = FULL_BLEED_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <div className={`layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <Zap size={22} className="sidebar-logo-icon" />
          {!collapsed && (
            <div className="sidebar-brand">
              <span className="sidebar-title">IoT Admin</span>
              <span className="sidebar-subtitle">GaliGali</span>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="sidebar-group">
              {!collapsed && <div className="sidebar-group-label">{group.label}</div>}
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={18} className="nav-icon" />
                    {!collapsed && <span className="nav-label">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <a
            href="https://iot-android-api.galigali.live"
            target="_blank"
            rel="noopener noreferrer"
            className="api-link"
            title={collapsed ? 'API Docs' : undefined}
          >
            <ExternalLink size={16} />
            {!collapsed && <span>API Docs</span>}
          </a>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <main className="main-content">
        {/* Crossing to V2. A plain <a>, not a <Link>: V2 mounts outside this
            Layout, so a real navigation is both correct and honest. */}
        <div className="v2-strip">
          <span className="v2-strip__badge">NEW</span>
          <span>A rebuilt console is in progress — same data, fewer places to look.</span>
          <a className="v2-strip__link" href="/v2">Try iot-admin V2</a>
          <span className="v2-strip__spacer" />
          <span className="v2-strip__muted">Classic stays exactly as it is.</span>
        </div>
        <div className={isFullBleed ? 'content-area content-area--full' : 'content-area'}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
